import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { useTheme } from "@material-ui/core";

import {
  HvButton,
  HvCheckBox,
  HvDialogActions,
  HvDialogContent,
  HvDialogTitle,
  HvGrid,
  HvTextArea,
  HvTypography,
} from "@hitachivantara/uikit-react-core";
import {
  Backwards,
  Calendar,
  CompletedStep,
  Forwards,
  Level0Good,
  Success,
  Time,
} from "@hitachivantara/uikit-react-icons";

import OverflowTooltip from "components/common/OverflowTooltip";
import LevelBadge from "components/common/LevelBadge";

import {
  completeSopStep,
  saveComentOnSopStep,
  startWorkOnSopStep,
  stopWorkOnSopStep,
  useIncidentTransactions,
  useMe,
  useTimeSettings,
} from "lib/api";
import { stringFallback } from "lib/utils/fallbacks";
import { formatInTimezone } from "lib/utils/datetime";
import { getUserDisplay } from "lib/utils/user";
import { getSopStatusColor } from "lib/utils/sop";
import { IncidentContext } from "lib/context/IncidentContext";

import useStyles from "./styles";

export interface SopStepDialogContentProps {
  sopId: string;
  stepIndex: number;
}

const DialogContent: React.FC<SopStepDialogContentProps> = ({
  sopId,
  stepIndex: initialStepIndex,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const { t } = useTranslation("sopStepDialog");

  const [stepIndex, setStepIndex] = useState(initialStepIndex);

  // eslint-disable-next-line camelcase
  const {
    incident: {
      pk_inc_id: incidentId = 0,
      inc_sop_map: incidentSops = [],
      inc_watchlist: incidentMembers = [],
    } = {},
  } = useContext(IncidentContext);

  const { data: sopStepsDetails, mutate } = useIncidentTransactions(
    `${incidentId}`
  );

  const me = useMe();

  const user = me.data?.data;
  const isMember = useMemo(
    () =>
      user?.id != null &&
      incidentMembers.some((member) => member.id === user.id),
    [incidentMembers, user?.id]
  );

  const steps = useMemo(() => {
    return incidentSops.find((sop) => sop.id === sopId)?.sop_steps ?? [];
  }, [incidentSops, sopId]);

  const step = steps[stepIndex];

  const currentlyWorkableStep = useMemo(() => {
    return steps.find((s) => {
      const stepDetails = sopStepsDetails?.data.find(
        (detail) => s.id === detail.fk_sop_step_id
      );

      if (stepDetails == null) {
        return true;
      }

      if (stepDetails.inct_status === "InProgress") {
        return true;
      }

      return false;
    });
  }, [sopStepsDetails?.data, steps]);

  const { status, stepDetails } = useMemo(() => {
    const transaction = sopStepsDetails?.data.find(
      (detail) => step.id === detail.fk_sop_step_id
    );

    const stepStatus: SopStatus =
      transaction?.inct_status ??
      (currentlyWorkableStep?.id === step.id ? "Unlocked" : "Locked");

    return {
      status: stepStatus,
      stepDetails: transaction,
    };
  }, [currentlyWorkableStep?.id, sopStepsDetails?.data, step.id]);

  const inProgressOrUnlocked = status === "InProgress" || status === "Unlocked";
  const noAssignee = stepDetails?.inct_assigned_to?.id == null;
  const isAssignee =
    user?.id != null && user.id === stepDetails?.inct_assigned_to.id;

  const formMode =
    inProgressOrUnlocked && (isAssignee || (noAssignee && isMember));
  const editMode = formMode && isAssignee;

  const statusColor = useMemo(
    () => getSopStatusColor(status, theme),
    [status, theme]
  );

  const dialogTitle = useMemo(() => {
    if (status === "Completed") {
      return (
        <>
          <Level0Good
            semantic="sema1"
            style={{ float: "left", width: 32, height: 32 }}
            height={22}
            width={22}
          />
          <HvTypography variant="xxsTitle" className={classes.title}>
            <OverflowTooltip data={stringFallback(step.name)} />
          </HvTypography>
        </>
      );
    }

    if (status === "InProgress") {
      return (
        <>
          <Success
            color="acce2"
            style={{ float: "left", width: 32, height: 32 }}
            height={22}
            width={22}
          />
          <HvTypography variant="xxsTitle" className={classes.title}>
            <OverflowTooltip data={stringFallback(step.name)} />
          </HvTypography>
        </>
      );
    }

    const textColor =
      status === "Locked"
        ? theme.hv.palette.atmosphere.atmo5
        : theme.hv.palette.accent.acce1;

    return (
      <>
        <LevelBadge
          color={statusColor}
          className={classes.levelBadge}
          style={{ float: "left", width: 32, height: 32 }}
          textIcon={`${((step.order ?? 0) + 1).toString()}`}
        />
        <HvTypography
          variant="xxsTitle"
          className={classes.title}
          style={{ color: textColor }}
        >
          <OverflowTooltip data={stringFallback(step.name)} />
        </HvTypography>
      </>
    );
  }, [
    status,
    theme.hv.palette.atmosphere.atmo5,
    theme.hv.palette.accent.acce1,
    statusColor,
    classes.levelBadge,
    classes.title,
    step.order,
    step.name,
  ]);

  const canGoPrevious = stepIndex > 0;
  const onPreviousHandler = useCallback(() => {
    if (canGoPrevious) {
      setStepIndex((previousStepIndex) => previousStepIndex - 1);
    }
  }, [canGoPrevious]);

  const canGoNext = stepIndex < steps.length - 1;
  const onNextHandler = useCallback(() => {
    if (canGoNext) {
      setStepIndex((previousStepIndex) => previousStepIndex + 1);
    }
  }, [canGoNext]);

  const textareaRef = useRef<HTMLTextAreaElement>();

  const assignToUserInProgress = useRef(false);
  const assignToUser = useCallback(
    async (evt, isToAssing: boolean) => {
      try {
        if (!assignToUserInProgress.current) {
          assignToUserInProgress.current = true;

          if (isToAssing) {
            await startWorkOnSopStep(incidentId, sopId, step.id as string);
          } else {
            // update the local data immediately
            mutate(async (cachedResponse) => {
              if (cachedResponse == null) {
                return cachedResponse;
              }

              const { data } = cachedResponse;

              const newData = data.filter((d) => d.fk_sop_step_id !== step.id);

              return { ...cachedResponse, data: newData };
            }, false);

            await stopWorkOnSopStep(incidentId, sopId, step.id as string);
          }

          mutate();

          assignToUserInProgress.current = false;
        }
      } catch (error) {
        assignToUserInProgress.current = false;
      }
    },
    [incidentId, mutate, sopId, step.id]
  );

  const completeStepInProgress = useRef(false);
  const completeStep = useCallback(async () => {
    try {
      if (!completeStepInProgress.current) {
        completeStepInProgress.current = true;

        await completeSopStep(
          incidentId,
          sopId,
          step.id as string,
          textareaRef.current?.value.trim() ?? ""
        );

        await mutate();

        onNextHandler();

        completeStepInProgress.current = false;
      }
    } catch (error) {
      completeStepInProgress.current = false;
    }
  }, [incidentId, onNextHandler, mutate, sopId, step.id]);

  const currentComment = stepDetails?.inct_value ?? "";
  const saveComment = useCallback(
    async (evt, value: string) => {
      const newComment = value.trim();

      if (newComment !== currentComment) {
        // update the local data immediately
        mutate(async (cachedResponse) => {
          if (cachedResponse == null) {
            return cachedResponse;
          }

          const { data } = cachedResponse;
          const changedIndex = data.findIndex(
            (d) => d.fk_sop_step_id === step.id
          );

          if (changedIndex === -1) {
            return cachedResponse;
          }

          const newData = [...data];
          newData[changedIndex].inct_value = value;

          return { ...cachedResponse, data: newData };
        }, false);

        const response = await saveComentOnSopStep(
          incidentId,
          sopId,
          step.id as string,
          newComment
        );

        // update the local data with the response
        mutate(async (cachedResponse) => {
          if (cachedResponse == null) {
            return cachedResponse;
          }

          const { data } = cachedResponse;
          const changedIndex = data.findIndex(
            (d) => d.fk_sop_step_id === step.id
          );

          if (changedIndex === -1) {
            return cachedResponse;
          }

          const newData = [...data];
          newData[changedIndex] = response.data;

          return { ...cachedResponse, data: newData };
        }, false);
      }
    },
    [currentComment, incidentId, mutate, sopId, step.id]
  );

  const { timezone } = useTimeSettings();

  return (
    <>
      <HvDialogTitle
        className={classes.dialogTitle}
        style={{ borderTopColor: statusColor }}
      >
        {dialogTitle}
      </HvDialogTitle>
      <HvDialogContent className={classes.dialogContent}>
        <HvGrid container>
          {formMode && (
            <>
              <HvGrid item xs>
                <HvCheckBox
                  label={t("assignToMeCheckbox")}
                  checked={status === "InProgress"}
                  disabled={!noAssignee && !isAssignee}
                  onChange={assignToUser}
                />
              </HvGrid>
              <HvGrid item xs={false}>
                <HvButton
                  category="secondary"
                  startIcon={<CompletedStep />}
                  disabled={noAssignee && !isAssignee}
                  onClick={completeStep}
                >
                  {t("complete")}
                </HvButton>
              </HvGrid>
            </>
          )}
          {!formMode && stepDetails != null && status === "InProgress" && (
            <HvGrid item xs={4}>
              <HvTypography
                variant="normalText"
                className={classes.smallContent}
              >
                {t("assignedTo", {
                  name: stringFallback(
                    getUserDisplay(stepDetails.inct_assigned_to)
                  ),
                })}
              </HvTypography>
            </HvGrid>
          )}
          {!formMode && stepDetails != null && status === "Completed" && (
            <>
              <HvGrid item xs={4}>
                <HvTypography
                  variant="highlightText"
                  component="h3"
                  className={classes.subtitle}
                >
                  <Calendar />
                  {t("completedDate")}
                </HvTypography>
                <HvTypography
                  variant="normalText"
                  className={classes.smallContent}
                  style={{ paddingLeft: 32 }}
                >
                  {stepDetails.inct_completed_date_time != null
                    ? formatInTimezone(
                        stepDetails.inct_completed_date_time,
                        timezone
                      )
                    : "—"}
                </HvTypography>
              </HvGrid>
              <HvGrid item xs={4}>
                <HvTypography
                  variant="highlightText"
                  component="h3"
                  className={classes.subtitle}
                >
                  {t("assignee")}
                </HvTypography>
                <HvTypography
                  variant="normalText"
                  className={classes.smallContent}
                >
                  {stringFallback(getUserDisplay(stepDetails.inct_assigned_to))}
                </HvTypography>
              </HvGrid>
              <HvGrid item xs={4}>
                <HvTypography
                  variant="highlightText"
                  component="h3"
                  className={classes.subtitle}
                >
                  <Time />
                  {t("duration")}
                </HvTypography>
                <HvTypography
                  variant="normalText"
                  className={classes.smallContent}
                  style={{ paddingLeft: 32 }}
                >
                  {moment(stepDetails.inct_start_date_time).from(
                    moment(stepDetails.inct_completed_date_time),
                    true
                  )}
                </HvTypography>
              </HvGrid>
            </>
          )}
          <HvGrid item xs={12}>
            <HvTypography
              variant="highlightText"
              component="h3"
              className={classes.subtitle}
            >
              {t("instructions")}
            </HvTypography>
            <HvTypography variant="normalText" className={classes.content}>
              {step.instructions}
            </HvTypography>
          </HvGrid>
          {!formMode && (
            <HvGrid item xs={12}>
              <HvTypography
                variant="highlightText"
                component="h3"
                className={classes.subtitle}
              >
                {t("comments")}
              </HvTypography>
              <HvTypography variant="normalText" className={classes.content}>
                {stringFallback(stepDetails?.inct_value)}
              </HvTypography>
            </HvGrid>
          )}
          {formMode && (
            <HvGrid item xs={12}>
              <HvTextArea
                // to clean when the user unassigns himself and the comments are cleared
                key={status}
                label={t("comments")}
                placeholder={t("commentsPlaceholder")}
                rows={5}
                disabled={!editMode}
                defaultValue={currentComment}
                onBlur={saveComment}
                inputRef={textareaRef}
              />
            </HvGrid>
          )}
        </HvGrid>
      </HvDialogContent>
      <HvDialogActions classes={{ spacing: classes.actionsSpacing }}>
        <HvButton
          category="ghost"
          startIcon={<Backwards />}
          onClick={onPreviousHandler}
          disabled={!canGoPrevious}
        >
          {t("previous")}
        </HvButton>
        <HvButton
          category="ghost"
          endIcon={<Forwards />}
          onClick={onNextHandler}
          disabled={!canGoNext}
        >
          {t("next")}
        </HvButton>
      </HvDialogActions>
    </>
  );
};

export default DialogContent;
